
import type { RxDBInstance } from '../../types/database';
import logger from './logger';
import { getSQLiteStorageInstance } from './sqliteStorageUtils';

/**
 * Creates manual database indexes that are not managed by RxDB's schema migration system.
 *
 * **Why this is necessary:**
 * - RxDB migrations would require migrating all user documents when adding/changing indexes
 * - This causes unacceptable performance issues for users with large document collections
 * - Manual index creation allows us to add indexes without document migration overhead
 *
 * **How to eventually remove this:**
 * - Add indexes to the appropriate DocumentIndexes arrays in indexes.ts
 * - Ensure new indexes follow the field ordering principles documented in indexes.ts
 * - We can skip having to check for max integer when using RxDB indexes
 *   (AND JSON_EXTRACT(data, '$.rxdbOnly.indexFields.parsed_doc_id') < 9007199254740991)
 *
 * NOTE: This function directly manipulates the underlying SQLite database, bypassing
 * RxDB's index management. When upgrading to the next major version of RxDB, these
 * manual indexes should be reviewed and potentially migrated to handle any schema
 * or table naming changes.
 */
export default async function createManualIndexes(database: RxDBInstance): Promise<void> {
  logger.info('Creating manual database indexes...');

  try {
    const { storageInstance, db, tableName } = await getSQLiteStorageInstance(database, 'documents');

    // Query all existing indexes for this table once
    const existingIndexes = await storageInstance.all(db, {
      query: `
        SELECT name FROM sqlite_master
        WHERE type='index'
        AND tbl_name=?
      `,
      params: [tableName],
      context: { method: 'queryExistingIndexes', data: tableName },
    });

    const existingIndexSet = new Set(
      (existingIndexes as unknown as { name: string; }[]).map((row) => row.name),
    );

    // Define all manual indexes in a centralized map
    const indexes = new Map([
      [
        'idx_docs_triage_original_filtering',
        `CREATE INDEX IF NOT EXISTS "idx_docs_triage_original_filtering" ON "${tableName}"(
          JSON_EXTRACT(data, '$.triage_status'),
          deleted,
          JSON_EXTRACT(data, '$.rxdbOnly.indexFields.parsed_doc_id')
        )
        WHERE deleted = 0
        AND JSON_EXTRACT(data, '$.rxdbOnly.indexFields.parsed_doc_id') IS NOT NULL
        AND JSON_EXTRACT(data, '$.rxdbOnly.indexFields.parsed_doc_id') < 9007199254740991`,
      ],
      [
        'idx_docs_triage_status_exists_last_status_update_desc',
        `CREATE INDEX IF NOT EXISTS "idx_docs_triage_status_exists_last_status_update_desc" ON "${tableName}" (
          JSON_EXTRACT(data, '$.rxdbOnly.indexFields.triage_status_exists'),
          JSON_EXTRACT(data, '$.rxdbOnly.indexFields.last_status_update_desc'),
          id
        ) WHERE JSON_EXTRACT(data, '$._deleted') = 0`,
      ],
    ]);

    // Create missing indexes
    for (const [indexName, indexExpression] of indexes) {
      if (existingIndexSet.has(indexName)) {
        logger.info(`Index ${indexName} already exists, skipping`);
        continue;
      }

      await storageInstance.run(db, {
        query: indexExpression,
        params: [],
        context: {
          method: 'createManualIndex',
          data: { name: indexName },
        },
      });

      logger.info(`Successfully created index: ${indexName}`);
    }

    logger.info('Manual database index creation completed');

  } catch (error) {
    logger.error('Failed to create manual indexes', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
